import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/axios';
import './CampaignAgent.css';

function actionLabel(type) {
  if (type === 'sendEmail') return 'E-mail';
  if (type === 'sendMessage') return 'LinkedIn-bericht';
  return 'LinkedIn-connectieverzoek';
}
function pct(value) { return value == null ? '—' : `${Number(value).toFixed(1)}%`; }
function performanceLabel(row) {
  const action = actionLabel(row.selectedAction);
  return `${action} · ${row.selectedChannel || 'onbekend'}${row.decisionChanged ? ' · AI override' : ''}`;
}
function evidenceLabel(row) {
  if (row?.evidenceLevel === 'strong') return 'Sterk bewijs';
  if (row?.evidenceLevel === 'usable') return 'Bruikbaar bewijs';
  if (row?.evidenceLevel === 'insufficient') return 'Nog te weinig data';
  return 'Geen uitvoeringen';
}

function CampaignAgentApprovals() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/agent/overview', { params: { campaignId: id, status: 'pending', limit: 500, performanceDays: 90 } });
      setCampaign((response.data.campaigns || []).find((item) => item._id === id) || null);
      setPerformance(response.data.performance || null);
      const rows = response.data.proposals || [];
      setProposals(rows);
      setEdits((current) => {
        const next = { ...current };
        rows.forEach((proposal) => {
          if (!next[proposal._id]) next[proposal._id] = {
            message: proposal.payload?.message || '',
            subject: proposal.payload?.subject || '',
            body: proposal.payload?.body || '',
          };
        });
        return next;
      });
      setSelected((current) => new Set([...current].filter((proposalId) => rows.some((row) => row._id === proposalId))));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const selectedProposals = useMemo(() => proposals.filter((proposal) => selected.has(proposal._id)), [proposals, selected]);
  const allSelected = proposals.length > 0 && proposals.every((proposal) => selected.has(proposal._id));
  const performanceRows = useMemo(() => (performance?.groups || []).slice(0, 8), [performance]);

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(proposals.map((proposal) => proposal._id)));
  const toggleOne = (proposalId) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(proposalId)) next.delete(proposalId); else next.add(proposalId);
    return next;
  });

  const payloadFor = (proposal) => {
    const edit = edits[proposal._id] || {};
    return proposal.actionType === 'sendEmail'
      ? { subject: edit.subject, body: edit.body }
      : { message: edit.message };
  };

  const approveOne = async (proposal, refresh = true) => {
    try {
      await api.patch(`/agent/proposals/${proposal._id}/approve`, payloadFor(proposal));
      if (refresh) { setNotice(`${actionLabel(proposal.actionType)} goedgekeurd en ingepland.`); await load(); }
      return { approved: true, stale: false };
    } catch (requestError) {
      const data = requestError.response?.data || {};
      if (data.error === 'PROPOSAL_STALE') {
        if (refresh) {
          setNotice(`Concept niet verstuurd: ${data.message || 'de leadcontext is veranderd'}. De agent plant opnieuw.`);
          await load();
        }
        return { approved: false, stale: true, message: data.message };
      }
      throw requestError;
    }
  };

  const rejectOne = async (proposal) => {
    setBusy(true); setError('');
    try {
      await api.patch(`/agent/proposals/${proposal._id}/reject`, { reason: 'Rejected from campaign approval page' });
      setNotice('Voorstel afgewezen.'); await load();
    } catch (requestError) { setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message); }
    finally { setBusy(false); }
  };

  const approveSelected = async () => {
    setBusy(true); setError('');
    let completed = 0; let stale = 0; const failures = [];
    for (const proposal of selectedProposals) {
      try {
        const result = await approveOne(proposal, false);
        if (result.approved) completed += 1;
        else if (result.stale) stale += 1;
      } catch (requestError) {
        failures.push(`${proposal.leadName}: ${requestError.response?.data?.message || requestError.response?.data?.error || requestError.message}`);
      }
    }
    setNotice(`${completed} voorstellen goedgekeurd.${stale ? ` ${stale} verouderde concept${stale === 1 ? '' : 'en'} automatisch ingetrokken en opnieuw gepland.` : ''}${failures.length ? ` ${failures.length} mislukt.` : ''}`);
    if (failures.length) setError(failures.slice(0, 5).join(' · '));
    await load(); setBusy(false);
  };

  const rejectSelected = async () => {
    if (!selectedProposals.length) return;
    setBusy(true); setError('');
    try {
      const response = await api.patch('/agent/proposals/bulk-reject', { proposalIds: selectedProposals.map((proposal) => proposal._id) });
      setNotice(`${response.data.rejectedCount || 0} voorstellen afgewezen.`); await load();
    } catch (requestError) { setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message); }
    finally { setBusy(false); }
  };

  return <main className="campaign-agent-page">
    <header className="campaign-agent-header">
      <div><Link to={`/campaigns/${id}/agent`} className="campaign-agent-back">← Terug naar AI-agent</Link><p className="campaign-agent-kicker">Approval queue</p><h1>{campaign?.title || 'Campagne'}</h1><p>Controleer LinkedIn- en e-mailconcepten voor deze campagne.</p></div>
      <button onClick={load} className="campaign-agent-button secondary" disabled={busy}>Vernieuwen</button>
    </header>
    {notice && <div className="campaign-agent-notice">{notice}</div>}{error && <div className="campaign-agent-error">{error}</div>}
    {performance && <section className="campaign-agent-card">
      <div className="campaign-agent-approval-head"><div><strong>AI decision performance</strong><p>Read-only observability over de laatste {performance.lookbackDays} dagen. Deze cijfers sturen de agent nog niet.</p>{!performance.totals?.evidenceSufficient && <p><strong>Let op:</strong> nog onvoldoende uitgevoerde acties voor betrouwbare vergelijking. Minimum: {performance.totals?.minimumCompleted || 20}.</p>}</div><span>{performance.totals?.completed || 0} uitgevoerd</span></div>
      <div className="campaign-agent-stats compact">
        <div><span>Approval</span><strong>{pct(performance.totals?.approvalRate)}</strong></div>
        <div><span>Execution success</span><strong>{pct(performance.totals?.executionSuccessRate)}</strong></div>
        <div><span>Reply</span><strong>{pct(performance.totals?.replyRate)}</strong></div>
        <div><span>Qualified</span><strong>{pct(performance.totals?.qualificationRate)}</strong></div>
        <div><span>Meeting booked</span><strong>{pct(performance.totals?.meetingBookingRate)}</strong></div>
        <div><span>Meeting completed</span><strong>{pct(performance.totals?.meetingCompletionRate)}</strong></div>
      </div>
      {performanceRows.length > 0 && <div style={{ overflowX: 'auto', marginTop: 16 }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}><thead><tr><th style={{ textAlign:'left' }}>Veilige keuze</th><th>Bewijs</th><th>Uitgevoerd</th><th>Reply</th><th>Qualified</th><th>Meeting</th><th>Completed</th><th>No-show</th></tr></thead><tbody>{performanceRows.map((row, index) => <tr key={`${row.choiceId || row.selectedAction}-${row.selectedChannel}-${index}`}><td>{performanceLabel(row)}</td><td style={{ textAlign:'center' }}>{evidenceLabel(row)}</td><td style={{ textAlign:'center' }}>{row.completed}</td><td style={{ textAlign:'center' }}>{pct(row.replyRate)}</td><td style={{ textAlign:'center' }}>{pct(row.qualificationRate)}</td><td style={{ textAlign:'center' }}>{pct(row.meetingBookingRate)}</td><td style={{ textAlign:'center' }}>{pct(row.meetingCompletionRate)}</td><td style={{ textAlign:'center' }}>{pct(row.noShowRate)}</td></tr>)}</tbody></table></div>}
    </section>}
    <section className="campaign-agent-card campaign-agent-approval-toolbar"><label><input type="checkbox" checked={allSelected} onChange={toggleAll} /> Selecteer alles</label><span>{selected.size} geselecteerd</span><button className="campaign-agent-button" disabled={busy || !selected.size} onClick={approveSelected}>Goedkeuren</button><button className="campaign-agent-button secondary" disabled={busy || !selected.size} onClick={rejectSelected}>Afwijzen</button></section>
    {loading ? <div className="campaign-agent-empty">Voorstellen laden…</div> : proposals.length === 0 ? <div className="campaign-agent-empty">Geen voorstellen wachten op goedkeuring.</div> : <section className="campaign-agent-approval-list">
      {proposals.map((proposal) => {
        const edit = edits[proposal._id] || {};
        return <article className="campaign-agent-card" key={proposal._id}>
          <div className="campaign-agent-approval-head"><label><input type="checkbox" checked={selected.has(proposal._id)} onChange={() => toggleOne(proposal._id)} /> {proposal.leadName}</label><span>{actionLabel(proposal.actionType)}</span></div>
          <p>{proposal.reason}</p>
          {proposal.actionType === 'sendEmail' ? <>
            <label>Onderwerp<input maxLength={150} value={edit.subject || ''} onChange={(event) => setEdits((current) => ({ ...current, [proposal._id]: { ...current[proposal._id], subject: event.target.value } }))} /></label>
            <label>E-mailtekst<textarea maxLength={5000} value={edit.body || ''} onChange={(event) => setEdits((current) => ({ ...current, [proposal._id]: { ...current[proposal._id], body: event.target.value } }))} /></label>
          </> : <label>LinkedIn-bericht<textarea maxLength={proposal.actionType === 'sendConnectionRequest' ? 300 : 1500} value={edit.message || ''} onChange={(event) => setEdits((current) => ({ ...current, [proposal._id]: { ...current[proposal._id], message: event.target.value } }))} /></label>}
          <div className="campaign-agent-header-actions"><button className="campaign-agent-button" disabled={busy} onClick={async () => { setBusy(true); setError(''); try { await approveOne(proposal); } catch (requestError) { setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message); } finally { setBusy(false); } }}>Goedkeuren en plannen</button><button className="campaign-agent-button secondary" disabled={busy} onClick={() => rejectOne(proposal)}>Afwijzen</button></div>
        </article>;
      })}
    </section>}
  </main>;
}

export default CampaignAgentApprovals;
